import { createFileRoute, Outlet, redirect, useRouteContext, useRouter } from "@tanstack/react-router";
import { WorkspaceLayout } from "@/layout/components/workspace-layout";
import { queryClient, trpc } from "@/utils/trpc";
import { useSubscription } from "@trpc/tanstack-react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { Channel, ExtendedUser, RouterAppContext, UserSession, Workspace } from "@/utils/custom-types";
import { authClient } from "@/lib/auth-client";
import { ServerWsEvent, type ServerEventMap } from "server/websocket-events";
import { toast } from "sonner";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/$workspaceId")({
	beforeLoad: async ({ context, location, params }) => {
		const { queryClient } = context;
		const session: UserSession | null = await queryClient.fetchQuery({
			queryKey: ["session"],
			queryFn: async () => {
				const { data } = await authClient.getSession();
				return data ?? null;
			}
		});
		const { workspaceId } = params;
		const { pathname } = location;

		if (!session) {
			throw redirect({
				to: "/sign-in",
				search: pathname ? { redirect: pathname } : undefined,
			});
		}

		const userWorkspaces: Workspace[] = session.user.userWorkspaces;

		const requestedWorkspace = userWorkspaces.find(
			(ws) => ws.id === Number(workspaceId),
		);

		if (!requestedWorkspace) {
			const fallbackWorkspaceId =
				userWorkspaces.find(
					(ws) => ws.id === Number(session.user.lastWorkspaceId)
				)?.id ?? userWorkspaces[0]?.id;

			if (!fallbackWorkspaceId) {
				throw redirect({ to: "/" });
			}

			throw redirect({
				to: "/$workspaceId",
				params: { workspaceId: String(fallbackWorkspaceId) },
			});
		}

		return { session: session, currentWorkspace: requestedWorkspace };
	},
	loader: async ({ context }) => {
		return {
			currentWorkspace: context.currentWorkspace,
			session: context.session, 
		} as {
			currentWorkspace: Workspace;
			session: UserSession;
		};
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { currentWorkspace, session } = Route.useLoaderData() as {
		currentWorkspace: Workspace;
		session: UserSession;
	};
	const [windowFocused, setWindowFocused] = useState(
		typeof document !== "undefined" ? document.hasFocus() : true
	);

	const mutateLastWorkspaceId = useMutation(
		trpc.me.updateLastWorkspace.mutationOptions({
			onMutate: () => {},
		}),
	);

	const router = useRouter();
	const channelsQueryKey = trpc.channel.getByUserAndWorkspace.queryKey({
		workspaceId: Number(currentWorkspace.id),
	});
	const notificationsAllowed =
		typeof window !== "undefined" &&
		"Notification" in window &&
		Notification.permission === "granted";

	useEffect(() => {
		const onFocus = () => setWindowFocused(true);
		const onBlur = () => setWindowFocused(false);

		window.addEventListener("focus", onFocus);
		window.addEventListener("blur", onBlur);

		return () => {
			window.removeEventListener("focus", onFocus);
			window.removeEventListener("blur", onBlur);
		};
	}, []);

	useEffect(() => {
		queryClient.resetQueries({
			predicate: (query) => {
				const protectedKey = ["session"];
			
				return JSON.stringify(query.queryKey) !== JSON.stringify(protectedKey);
			},
		});
	}, [currentWorkspace.id]);

	useSubscription(
		trpc.channel.subscribeToChannelRedis.subscriptionOptions(undefined, {
			onData: (event) => {
				if (event?.type === ServerWsEvent.ChannelOrderChanged || event?.type === ServerWsEvent.ChannelCreated || event?.type === ServerWsEvent.ChannelNameUpdated || event?.type === ServerWsEvent.ChannelDeleted) {
					// console.log(`[received ${event.type} event REDIS]`, JSON.stringify(event.data, null, 2));
					queryClient.invalidateQueries({ queryKey: channelsQueryKey });
				}
				// } else if (event?.type === ServerWsEvent.ChannelDeleted) {
				// 	const data = event.data as unknown as ServerEventMap[ServerWsEvent.ChannelDeleted];

				// 	queryClient.setQueryData(channelsQueryKey, (prev: Channel[] | undefined) => {
				// 		if (!prev) return prev;
						
				// 		return prev.map((c) => 
				// 			c.id === data.channelId ?
				// 				{
				// 					...c,
				// 					archived: true
				// 				} : c
				// 		);
				// 	});
				// };
			},
			onError: (err: unknown) => {
				console.error("[channel event error]", err);
			},
		}),
	);

	useSubscription(
		trpc.workspace.subscribeToWorkspaceRedis.subscriptionOptions(undefined, {
			onData: (event) => {
				if (event?.type === ServerWsEvent.WorkspaceCreated) {
					const data = event.data as unknown as ServerEventMap[ServerWsEvent.WorkspaceCreated];
					// console.log("[received workspace created event]", JSON.stringify(data, null, 2));
					const workspacesQueryKey = trpc.workspace.getUserWorkspaces.queryKey();

					queryClient.invalidateQueries({ queryKey: workspacesQueryKey });

					if (data.createdBy === session.user.id) {
						mutateLastWorkspaceId.mutate({ workspaceId: data.workspaceId });
						queryClient.invalidateQueries({queryKey: ["session"]});
						router.navigate({ to: `/${data.workspaceId}` });
					}
				} else if (event?.type === ServerWsEvent.WorkspaceMemberAdded) {
					const data = event.data as unknown as ServerEventMap[ServerWsEvent.WorkspaceMemberAdded];
					if (data.member.userId === session.user.id) {
						// console.log("[received workspace member added event]", JSON.stringify(data, null, 2));
						if (data.addedBy !== session.user.id) {
							toast(`You have been added to the ${data.workspaceTitle} workspace.`, {
								action: {
									label: "Go to",
									onClick: () => {
										mutateLastWorkspaceId.mutate({ workspaceId: data.workspaceId });
										router.navigate({ to: `/${data.workspaceId }` });
									}
								} 
							});
						}

						queryClient.invalidateQueries({queryKey: ["session"]});
						const workspacesQueryKey = trpc.workspace.getUserWorkspaces.queryKey();

						queryClient.invalidateQueries({ queryKey: workspacesQueryKey });
					} else if (data.addedBy !== session.user.id) {
						const workspaceMembersQueryKey = trpc.workspace.getMembers.queryKey({
							workspaceId: Number(currentWorkspace.id),
						});

						queryClient.invalidateQueries({ queryKey: workspaceMembersQueryKey })
					}
				} else if (event?.type === ServerWsEvent.WorkspaceMemberRemoved) {
					const data = event.data as unknown as ServerEventMap[ServerWsEvent.WorkspaceMemberRemoved];
					if (data.userId === session.user.id) {
						// console.log("[received workspace member removed event]", JSON.stringify(data, null, 2));
						if (data.workspaceId === currentWorkspace.id) {
							toast.error(`You have been removed from the ${data.workspaceTitle} workspace.`);

							const nextWorkspaceId = session.user.lastWorkspaceId;

							if (nextWorkspaceId) {
								mutateLastWorkspaceId.mutate({ workspaceId: nextWorkspaceId });
								router.navigate({ to: `/${nextWorkspaceId}` });
							}
						} else {
							toast(`You have been removed from the ${data.workspaceTitle} workspace.`);
						}

						queryClient.invalidateQueries({queryKey: ["session"]});
						const workspacesQueryKey = trpc.workspace.getUserWorkspaces.queryKey();

						queryClient.invalidateQueries({ queryKey: workspacesQueryKey });
					} else if (data.removedBy !== session.user.id) {
						const workspaceMembersQueryKey = trpc.workspace.getMembers.queryKey({
							workspaceId: Number(currentWorkspace.id),
						});

						queryClient.invalidateQueries({ queryKey: workspaceMembersQueryKey })
					}
				} else if (event?.type === ServerWsEvent.WorkspaceMemberLeft) {
					const data = event.data as unknown as ServerEventMap[ServerWsEvent.WorkspaceMemberLeft];
					// console.log("[received workspace member left event]", JSON.stringify(data, null, 2));
					const workspaceMembersQueryKey = trpc.workspace.getMembers.queryKey();
					queryClient.invalidateQueries({ queryKey: workspaceMembersQueryKey });
				}
			},
			onError: (err: unknown) => {
				console.error("[workspace event error]", err);
			},
		}),
	);

	useSubscription(
		trpc.message.subscribeToMessagesRedis.subscriptionOptions(undefined, {
			onData: (event) => {
				if (event?.type === ServerWsEvent.MessageCreated) {
					const data = event.data as unknown as ServerEventMap[ServerWsEvent.MessageCreated];
					// console.log("[received messageCreated event]", JSON.stringify(data, null, 2));
					if (data.userId === session.user.id) return;

					const channelList = queryClient.getQueryData(channelsQueryKey) as Channel[] | undefined;
					const targetChannel = channelList?.find((c) => c.id === data.channelId);
					const isChatChannel = ["message-group", "self-dm", "dm"].includes(targetChannel?.type ?? "");
					const currentPath = router.state.location.pathname;
					const isActiveChannel = currentPath.includes(`/chats/${data.channelId}`);
					const shouldIncrementUnread = isChatChannel && (!isActiveChannel || !windowFocused);

					const threadRootId = data.message.threadRootId

					const messagesQueryKey = trpc.message.getByChannel.queryKey({
						channelId: Number(data.channelId)
					});

					const threadMessagesQueryKey = trpc.message.getByThread.queryKey({
						threadId: threadRootId
					});

					const queryKeyToUpdate = threadRootId ? threadMessagesQueryKey : messagesQueryKey;
					const lastReadMeta = threadRootId
						? {}
						: {
							lastReadMessageId: targetChannel?.lastReadMessageId ?? null,
							lastReadAt: targetChannel?.lastReadAt ?? null,
						};

					if (shouldIncrementUnread) {
						queryClient.setQueryData(channelsQueryKey, (prev: Channel[] | undefined) => {
							if (!prev) return prev;
							const updated = prev.map((c) => {
								if (c.id === data.channelId) {
									const newUnreadCount = Number(c.unreadCount ?? 0) + 1;
									return { ...c, unreadCount: newUnreadCount };
								}
								return c;
							});
							return updated;
						});
					}

					if (!windowFocused && notificationsAllowed && (data.workspaceId === currentWorkspace.id)) {
						const senderName = data.userName || "Someone";
						const channelTitle = targetChannel?.title?.trim();
						const channelLabel = channelTitle
							? `#${channelTitle}`
							: targetChannel?.type === "dm" || targetChannel?.type === "self-dm"
								? "Direct message"
								: "Chat";
						const text = data.message.text?.trim();
						const body = text?.length
							? text
							: data.message.attachments?.length
								? "Sent an attachment."
								: "Sent a message.";

						new Notification(`${senderName} in ${channelLabel}`, { body });
					}

					if (threadRootId) {
						queryClient.setQueryData(messagesQueryKey, (prev: any) => {
							if (!prev?.pages) return prev;
				
							const pages = prev.pages.map((p: any) => ({
								...p,
								messages: p.messages.map((m: any) =>
									m.id === data.message.threadRootId ? 
										{ 
											...m, 
											replyCount: Number(m.replyCount ?? 0) + 1,
											lastReplyAt: data.message.createdAt,
											lastReplySenderId: data.message.senderId
										} : m
								),
							}));
				
							return { ...prev, pages };
						});
					}

					queryClient.setQueryData(queryKeyToUpdate, (prev: any) => {
						const optimisticMessage = {
							id: data.message.id,
							senderId: data.userId,
							workspaceId: Number(data.workspaceId),
							channelId: Number(data.channelId),
							senderName: data.userName,
							text: data.message.text,
							attachments: data.message.attachments,
							createdAt: data.message.createdAt,
							threadRootId: data.message.threadRootId
						};

					if (!prev || prev.pages.length === 0) {
						return {
							__subscriptionSeeded: true,
							pages: [
								{
									messages: [optimisticMessage],
									hasMore: false,
									nextCursor: undefined,
									...lastReadMeta,
								},
							],
							pageParams: [undefined],
							}
						}
			
						const pages = [...prev.pages];
						const firstPage = pages[0] ?? { messages: [], hasMore: false, nextCursor: undefined };
			
						pages[0] = {
							...firstPage,
							messages: [...(firstPage.messages ?? []), optimisticMessage],
						};
			
						return { ...prev, pages };
					});

					// queryClient.invalidateQueries({ 
					// 	queryKey: messagesQueryKey,
					// 	refetchType: "none",
					// });
				} else if (event?.type === ServerWsEvent.MessageDeleted) {
					const data = event.data as unknown as ServerEventMap[ServerWsEvent.MessageDeleted];

					if (data.userId === session.user.id) return;

					const messagesQueryKey = trpc.message.getByChannel.queryKey({
						channelId: Number(data.channelId)
					});

					queryClient.setQueryData(messagesQueryKey, (prev: any) => {
						if (!prev?.pages.length) return prev;
			
						const pages = prev.pages.map((page: any) => {
							return {
								...page,
								messages: (page.messages ?? []).filter((m: any) => m.id !== data.messageId),
							};
						});
						return { ...prev, pages };
					});

				} else if (event?.type === ServerWsEvent.MessageUpdated) {
					const data = event.data as unknown as ServerEventMap[ServerWsEvent.MessageUpdated];

					if (data.userId === session.user.id) return;

					const messagesQueryKey = trpc.message.getByChannel.queryKey({
						channelId: Number(data.channelId)
					});

					queryClient.setQueryData(messagesQueryKey, (prev: any) => {
						if (!prev?.pages) return prev;
			
						const pages = prev.pages.map((p: any) => ({
							...p,
							messages: p.messages.map((m: any) =>
								m.id === data.message.id ? 
									{ 
										...m, 
										text: data.message.text,
										editedAt: data.message.editedAt
									} : m
							),
						}));
			
						return { ...prev, pages };
					});
				}
			},
			onError: (err: unknown) => {
				console.error("[channel event error]", err);
			},
		}),
	);

	return (
		<WorkspaceLayout />
	);
}
